/**
 * Tenký API klient pro admin UI — obaluje fetch(), aby vždy vracel JSON nebo typovanou chybu.
 * Všechny mutace jdou přes POST/PUT/PATCH. Při offline odloží do IndexedDB fronty.
 *
 * Pozn.: VŠECHNY API endpointy jsou pod `/api/*` prefixem — oddělené od SPA rout
 * (`/players/:id`, `/admin/...`), takže nikdy nedojde ke kolizi HTML×JSON na stejné
 * cestě. Volající používají cesty BEZ `/api` (např. `/players/123`); klient prefix
 * předřadí sám. Vite proxy + server routují `/api/*` na backend.
 */

import { refreshAccessToken, handleSessionExpired } from "./use-auth.js";

/** Společný prefix všech API endpointů (odděluje API od SPA rout). */
const API_BASE = "/api";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /** Surový `error` objekt z response — obsahuje volitelné details
     *  (např. `conflictingUser` u 409 conflict_user_nickname). */
    readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// Redirect na /login po neobnovitelné expiraci řeší sdílený `handleSessionExpired`
// z use-auth (používá ho i useWs). Neredirectuje na /login a /signup.

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  _retried = false,
): Promise<T> {
  // Content-Type: application/json POUZE když reálně posíláme tělo. Fastify
  // odmítá prázdné tělo s tímto content-typem ("Body cannot be empty when
  // content-type is set to 'application/json'") — týká se DELETE a POST bez body.
  const headers: Record<string, string> = {};
  const token = localStorage.getItem("bw_token");
  if (token) headers["Authorization"] = `Bearer ${token}`;
  if (body !== undefined) headers["Content-Type"] = "application/json";
  headers["Accept"] = "application/json";

  // `/api` prefix odděluje API od SPA rout (žádná kolize HTML×JSON). Cesta volajícího
  // začíná "/" bez `/api` — prefix předřadíme zde.
  const res = await fetch(API_BASE + path, {
    method,
    headers,
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    ...(signal !== undefined ? { signal } : {}),
  });

  if (!res.ok) {
    let code = "UNKNOWN";
    let message = res.statusText;
    let details: Record<string, unknown> | undefined;
    try {
      // Backend konzistentně posílá obálku { error: { code, message, ...extra } } (viz auth, beer-logs, …).
      // Akceptujeme i plochou variantu pro budoucí kompatibilitu.
      const json = (await res.json()) as
        | { error?: Record<string, unknown>; code?: string; message?: string }
        | null;
      const errObj = json?.error ?? {};
      code = (errObj["code"] as string) ?? json?.code ?? code;
      message = (errObj["message"] as string) ?? json?.message ?? message;
      // Zachovej i ostatní pole pro UI (např. conflictingUser u 409).
      if (json?.error) details = json.error;
    } catch {
      // noop
    }

    // 401 = JWT chybí / vypršel / je neplatný. Frontend stejně nemá refresh
    // interceptor (TODO task D-4), tak při expiraci provedeme cleanup auth
    // stavu a přesměrujeme na /login s `?next=` na aktuální cestu, aby se user
    // po přihlášení vrátil tam, kde byl. Loop na samotné /login stránce
    // zabraňujeme kontrolou pathname.
    //
    // POZN: handleSessionExpired se volá i pokud token chybí (typicky refresh
    // stránky s vyprázdněným localStorage, nebo race condition po logout).
    // Bez tohoto fallbacku se uživateli zobrazí backend message
    // "Chybí nebo neplatný Bearer token." místo přesměrování na login.
    if (res.status === 401) {
      // Pokus o obnovu access tokenu (jednou) přes refresh token — drží session
      // naživu místo okamžitého odhlášení. Ne pro /auth/* (refresh/login samy
      // legitimně 401ují) a ne při už-retried requestu.
      if (!_retried && !path.startsWith("/auth/")) {
        const newToken = await refreshAccessToken();
        if (newToken) {
          return request<T>(method, path, body, signal, true);
        }
      }
      handleSessionExpired();
    }

    throw new ApiError(res.status, code, message, details);
  }

  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

/**
 * Stáhne soubor přes fetch s Auth headerem a vytvoří <a download> klik.
 * Řeší případy, kdy `window.open(path)` selhává kvůli neposlanému Authorization
 * (např. /seasons/:id/archive/download vyžaduje requireAuth).
 */
async function downloadBlob(path: string, filename: string): Promise<void> {
  const token = localStorage.getItem("bw_token") ?? localStorage.getItem("bw:token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(API_BASE + path, { method: "GET", headers });
  if (!res.ok) {
    if (res.status === 401) handleSessionExpired();
    throw new ApiError(res.status, "DOWNLOAD_FAILED", `HTTP ${res.status} ${res.statusText}`);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Načte binární zdroj (obrázek) přes fetch s Auth headerem a vrátí object URL pro
 * `<img src>`. Volající MUSÍ URL po použití uvolnit přes `URL.revokeObjectURL`.
 * Řeší endpointy vyžadující Bearer token, kam `<img>` token sám neposlal.
 */
async function getObjectUrl(path: string, signal?: AbortSignal): Promise<string> {
  const token = localStorage.getItem("bw_token") ?? localStorage.getItem("bw:token");
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(API_BASE + path, { method: "GET", headers, ...(signal ? { signal } : {}) });
  if (!res.ok) {
    if (res.status === 401) handleSessionExpired();
    throw new ApiError(res.status, "FETCH_FAILED", `HTTP ${res.status} ${res.statusText}`);
  }
  return URL.createObjectURL(await res.blob());
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal) => request<T>("GET", path, undefined, signal),
  post: <T>(path: string, body: unknown) => request<T>("POST", path, body),
  put: <T>(path: string, body: unknown) => request<T>("PUT", path, body),
  patch: <T>(path: string, body: unknown) => request<T>("PATCH", path, body),
  delete: <T>(path: string, body?: unknown) => request<T>("DELETE", path, body),
  downloadBlob,
  getObjectUrl,
};
